/**
 * `/admin/users` — TASK 10.4 Steps 1-3 (US-2: AC 2.1 / 2.3 / 2.6 / 2.9 /
 * 2.10 / 2.11 / 2.12).
 *
 * The mock's `_adminU(V)` view model (L1123-1133) and template (L649-676),
 * wired to the real admin API (TASK 2.4) instead of `state.allow`:
 *
 *   - users table: email (` · YOU` on the caller's row) / role chip / status
 *     chip / "Added … · Last active …" meta line;
 *   - add-user form with INLINE email validation (AC 2.3) — required, shape,
 *     already-invited — before any request leaves the device (the server
 *     re-validates regardless);
 *   - Promote/Demote, Remove/Reactivate, Delete-data… per row;
 *   - **self-row guard (AC 2.6/2.10):** the caller's own row has Demote /
 *     Remove / Delete-data DISABLED with explanatory tooltips. Cosmetic
 *     defence only — `services/admin-users.ts` answers `self_forbidden`
 *     server-side, which is the real control;
 *   - loading skeleton + retriable error (AC 2.11);
 *   - **optimistic mutations (AC 2.12):** every row change applies
 *     immediately, and a failure ROLLS THE ROW BACK to the pre-mutation
 *     snapshot with an announced error toast (mock `_optim`, L1133);
 *   - Delete-data opens ./DeleteDataModal.js — typed-confirmation (AC 2.9),
 *     focus-trapped by the OverlayStack.
 *
 * ── WHO IS "SELF"? ────────────────────────────────────────────────────────
 * `GET /api/me` carries no email, so selfness is decided by USER id: the row
 * whose `userId` (the linked account id on the allowlist entry) equals the
 * session's `userId`. Un-linked rows (`userId: null` — invited, never signed
 * in) can never be self: the caller, being signed in, is linked by
 * definition.
 *
 * Admin nav visibility (AC 2.7) is the SHELL's: MoreMenu hides the Admin row
 * for members and the router bounces direct `/admin*` hits to
 * `/not-authorized` — neither is an authorization control (requireAdmin on
 * the server is); this view assumes it only ever mounts for an admin.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 10.4's files */

import { TESTIDS, ordinalTestId } from '@streamymusic/shared';

import { fieldErrorText } from '../../../api/api-client.js';
import { ErrorBlock, LoadingBlock } from '../../components/StateBlocks.js';
import { Pill } from '../../components/primitives/Pill.js';
import { append, el } from '../../components/primitives/dom.js';

import { AdminNav } from './AdminNav.js';
import { openDeleteDataModal } from './DeleteDataModal.js';

/**
 * A household allowlist fits in one read: the server pages at ≤ 200 and the
 * PRD caps the audience far below it, so the view reads a single page and
 * treats `total` as authoritative for the count label.
 */
export const USERS_PAGE_LIMIT = 200;

/** mock `_validEmail` (L1125) — the inline shape check, verbatim. */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** mock `_addUser` (L1126) — inline validation copy, verbatim. */
export const EMAIL_REQUIRED_ERROR = 'Enter an email address.';
export const EMAIL_INVALID_ERROR = 'That doesn’t look like a valid email address.';
/**
 * @param {string} email
 * @returns {string}
 */
export const alreadyInvitedError = (email) => `“${email}” is already invited.`;

/** mock `_adminU` row tooltips (L1124) — the self-row guard copy, verbatim. */
export const SELF_ROLE_TOOLTIP = 'You can’t change your own role';
export const SELF_REMOVE_TOOLTIP = 'You can’t remove yourself';
export const SELF_DELETE_TOOLTIP = 'You can’t delete your own data here';

/** mock `_optim` call sites (L1126-1131) — toast copy, verbatim. */
export const ROLE_UPDATED_MESSAGE = 'Role updated';
export const ROLE_FAILED_MESSAGE = 'Couldn’t change role — rolled back.';
export const USER_REMOVED_MESSAGE = 'User removed';
export const USER_REACTIVATED_MESSAGE = 'User reactivated';
export const UPDATE_FAILED_MESSAGE = 'Couldn’t update — rolled back.';
export const INVITE_FAILED_MESSAGE = 'Couldn’t add user — rolled back.';
export const REACTIVATE_FAILED_MESSAGE = 'Couldn’t reactivate — rolled back.';
/** mock `_ddm` submit (L1149) / net-new failure copy (the mock never failed a purge). */
export const PURGE_FAILED_MESSAGE = 'Couldn’t purge data — nothing was deleted.';

/** mock L664/L665 — status colour map (border + text, never colour alone). */
const STATUS_COLOR = {
  active: 'var(--sm-color-teal)',
  invited: 'var(--sm-color-yellow)',
  removed: 'var(--sm-color-pink)',
};

/**
 * One allowlist row as this view consumes it: the shared `AdminUser` DTO
 * plus `userId` — the linked account id (null until first sign-in) that the
 * self-row guard compares against the session's `userId`.
 *
 * @typedef {import('@streamymusic/shared').AdminUser & { userId?: string|null }} AdminUserRow
 */

/**
 * The admin-users API surface this view drives. Matches the TASK 2.4 routes
 * one-for-one (`apps/api/src/routes/admin-users.ts`); the shared ApiClient
 * grows these methods in the composition root's wiring step.
 *
 * @typedef {object} AdminUsersApi
 * @property {(options?: { limit?: number, offset?: number, signal?: AbortSignal }) =>
 *   Promise<{ items: AdminUserRow[], total: number }>} listAdminUsers
 *   `GET /api/admin/users`
 * @property {(email: string) => Promise<{ outcome: 'created'|'already_invited'|'reactivated' }>} addAdminUser
 *   `POST /api/admin/users` — add or reactivate by email
 * @property {(entryId: string, role: 'admin'|'member') => Promise<{ outcome: 'ok' }>} setAdminUserRole
 *   `POST /api/admin/users/:id/role`
 * @property {(entryId: string) => Promise<{ outcome: 'ok' }>} removeAdminUser
 *   `POST /api/admin/users/:id/remove`
 * @property {(entryId: string) => Promise<{ outcome: 'ok' }>} reactivateAdminUser
 *   `POST /api/admin/users/:id/reactivate`
 * @property {(entryId: string, confirm: string) => Promise<{ outcome: 'ok' }>} deleteAdminUserData
 *   `POST /api/admin/users/:id/delete-data` — typed confirmation forwarded verbatim
 */

/**
 * mock L663 — "Added Mar 2 · Last active Today" style short dates. The mock
 * seeded prose; production formats the ISO timestamps the API returns.
 *
 * @param {string|null} iso
 * @returns {string}
 */
export function formatDay(iso) {
  if (iso === null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Builds the `/admin/users` section and starts loading the allowlist.
 *
 * @param {{
 *   api: AdminUsersApi,
 *   heading: HTMLElement,
 *   overlays: import('../../app/OverlayStack.js').OverlayStackHandle,
 *   selfUserId: string|null,
 *   toast: (text: string, tone?: 'ok'|'err'|'info') => void,
 *   announce?: (text: string) => void,
 *   limit?: number,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` — handed in by
 *   the shell, never created here. `selfUserId` is `state.session.userId`.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function UsersView(options) {
  const { api, heading, overlays, selfUserId, toast, announce, limit = USERS_PAGE_LIMIT } = options;

  heading.textContent = 'THE GUEST LIST';

  const section = el('section', { attrs: { 'data-screen-label': 'Admin users' } });
  append(section, heading, AdminNav({ active: 'users' }));

  /* ── invite form (mock L652-656; AC 2.3 inline validation) ────────────── */

  const input = el('input', {
    className: 'sm-input',
    testId: TESTIDS.EMAIL_TO_INVITE_INPUT,
    attrs: {
      id: 'sm-invite',
      type: 'text',
      placeholder: 'name@example.com',
      'aria-describedby': 'sm-invite-error',
    },
  });
  const inviteButton = el('button', {
    className: 'sm-btn sm-btn--retry sm-btn--display-17',
    text: '+ INVITE',
    testId: TESTIDS.INVITE_USER_BUTTON,
    attrs: { type: 'submit' },
  });
  const form = el('form', {
    className: 'sm-form-panel',
    on: {
      submit: (event) => {
        event.preventDefault();
        submitInvite();
      },
    },
  });
  append(
    form,
    // mock L653 — visually hidden label; the input is named, not placeholder-only.
    el('label', {
      className: 'sm-visually-hidden',
      text: 'Email to invite',
      attrs: { for: 'sm-invite' },
    }),
    input,
    inviteButton,
  );

  // mock L657 — role=alert so the inline failure is announced (AC 2.3).
  const fieldError = el('p', {
    className: 'sm-form-error',
    attrs: { id: 'sm-invite-error', role: 'alert', hidden: true },
  });

  // mock L658 — "8 users on the allowlist".
  const countLabel = el('p', {
    className: 'sm-count-label sm-count-label--list',
    attrs: { hidden: true },
  });

  const body = el('div');
  append(section, form, fieldError, countLabel, body);

  /* ── state ────────────────────────────────────────────────────────────── */

  /** @type {AdminUserRow[]} */
  let items = [];
  let total = 0;
  let generation = 0;
  let disposed = false;
  /** @type {AbortController|null} */
  let controller = null;
  /** One optimistic mutation at a time — a second would corrupt the snapshot. */
  let mutating = false;

  /** @param {string} text */
  function showFieldError(text) {
    fieldError.textContent = text;
    fieldError.removeAttribute('hidden');
    if (announce !== undefined) announce(text);
  }

  function clearFieldError() {
    fieldError.textContent = '';
    fieldError.setAttribute('hidden', '');
  }

  /**
   * The invite form's controls are NOT rebuilt by paint() (only the rows are),
   * so their disabled state is toggled directly from runMutation's enter/exit.
   * While a mutation is in flight the invite input + submit button are disabled
   * so a concurrent submit can never fire — and thus can never clear and lose a
   * value that a `mutating`-guarded runMutation would silently drop.
   *
   * @param {boolean} disabled
   */
  function setFormDisabled(disabled) {
    /** @type {HTMLInputElement} */ (input).disabled = disabled;
    /** @type {HTMLButtonElement} */ (inviteButton).disabled = disabled;
  }

  /* ── rows (mock L660-673) ─────────────────────────────────────────────── */

  /**
   * @param {AdminUserRow} user
   * @param {{ demote: number, promote: number, remove: number, reactivate: number, del: number }} ordinals
   * @returns {HTMLElement}
   */
  function UserRow(user, ordinals) {
    const self = selfUserId !== null && (user.userId ?? null) === selfUserId;
    const admin = user.role === 'admin';
    const removed = user.status === 'removed';

    const card = el('div', { className: 'sm-panel sm-panel--flat' });

    // header: email (+ · YOU) / meta / role pill / status pill
    const primary = el('span', { className: 'sm-line-primary', text: user.email });
    if (self) primary.append(el('span', { className: 'sm-self-marker', text: ' · YOU' }));
    const emailCol = append(
      el('span', { className: 'sm-flex-fill--140' }),
      primary,
      el('span', {
        className: 'sm-row-meta',
        text: `Added ${formatDay(user.invitedAt)} · Last active ${formatDay(user.lastActiveAt)}`,
      }),
    );
    const head = append(
      el('div', { className: 'sm-row-head' }),
      emailCol,
      Pill({
        label: admin ? 'ADMIN' : 'MEMBER',
        background: admin ? 'var(--sm-color-pink)' : 'var(--sm-color-teal)',
        className: 'sm-pill--wide',
      }),
      Pill({
        label: user.status.toUpperCase(),
        tone: 'status',
        color: STATUS_COLOR[user.status] ?? 'var(--sm-color-teal)',
      }),
    );

    /**
     * mock L668-670 — one row-action button. Self-guarded controls are
     * DISABLED with an explanatory `title` tooltip (AC 2.6/2.10) and dimmed
     * (mock `selfOpacity`); status is still carried by the disabled state +
     * tooltip text, never opacity alone.
     *
     * @param {{
     *   label: string,
     *   testId: string,
     *   color?: string,
     *   danger?: boolean,
     *   selfBlocked: boolean,
     *   tooltip: string,
     *   onClick: () => void,
     * }} spec
     * @returns {HTMLElement}
     */
    const actionButton = ({ label, testId, color, danger = false, selfBlocked, tooltip, onClick }) => {
      // mock `{{ u.selfOpacity }}` / `{{ u.removeCol }}` — dynamic values, so
      // CSSOM via styleProps (CSP contract), merged so neither drops the other.
      const styleProps = {
        ...(selfBlocked ? { opacity: '.45' } : {}),
        ...(color === undefined ? {} : { color }),
      };
      // Disabled while ANOTHER mutation is in flight (one optimistic mutation at
      // a time — a concurrent row change would corrupt the snapshot). Only the
      // self-row guard carries the explanatory tooltip; the mutating-disable is
      // transient and needs none.
      const disabled = selfBlocked || mutating;
      return el('button', {
        className: danger
          ? 'sm-btn sm-btn--row-action sm-btn--row-action-danger'
          : 'sm-btn sm-btn--row-action sm-btn--row-action-teal',
        text: label,
        testId,
        attrs: {
          type: 'button',
          ...(disabled ? { disabled: true } : {}),
          ...(selfBlocked ? { 'aria-disabled': 'true', title: tooltip } : {}),
        },
        ...(Object.keys(styleProps).length === 0 ? {} : { styleProps }),
        on: { click: () => onClick() },
      });
    };

    const roleButton = actionButton({
      label: admin ? 'Demote' : 'Promote',
      testId: admin
        ? ordinalTestId(TESTIDS.DEMOTE_BUTTON, (ordinals.demote += 1))
        : ordinalTestId(TESTIDS.PROMOTE_BUTTON, (ordinals.promote += 1)),
      selfBlocked: self,
      tooltip: SELF_ROLE_TOOLTIP,
      onClick: () => roleMutation(user),
    });
    const removeButton = actionButton({
      label: removed ? 'Reactivate' : 'Remove',
      testId: removed
        ? ordinalTestId(TESTIDS.REACTIVATE_BUTTON, (ordinals.reactivate += 1))
        : ordinalTestId(TESTIDS.REMOVE_BUTTON, (ordinals.remove += 1)),
      color: removed ? 'var(--sm-color-teal)' : 'var(--sm-color-yellow)',
      selfBlocked: self,
      tooltip: SELF_REMOVE_TOOLTIP,
      onClick: () => removeMutation(user),
    });
    const deleteButton = actionButton({
      label: 'Delete data…',
      testId: ordinalTestId(TESTIDS.DELETE_DATA_BUTTON, (ordinals.del += 1)),
      danger: true,
      selfBlocked: self,
      tooltip: SELF_DELETE_TOOLTIP,
      onClick: () => openDeleteData(user),
    });

    append(
      card,
      head,
      append(el('div', { className: 'sm-row-actions' }), roleButton, removeButton, deleteButton),
    );
    return card;
  }

  function paint() {
    countLabel.textContent = `${total} users on the allowlist`;
    countLabel.removeAttribute('hidden');

    const list = el('div', { className: 'sm-stack-8' });
    const ordinals = { demote: 0, promote: 0, remove: 0, reactivate: 0, del: 0 };
    for (const user of items) list.append(UserRow(user, ordinals));
    body.replaceChildren(list);
  }

  /* ── loading (AC 2.11) ────────────────────────────────────────────────── */

  /**
   * @param {{ quiet?: boolean }} [opts]
   *   `quiet` refreshes after a successful mutation — no skeleton, and a
   *   failed refresh keeps the (already correct) optimistic rows on screen.
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const quiet = opts.quiet === true;
    const own = (generation += 1);
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    if (!quiet) {
      countLabel.setAttribute('hidden', '');
      body.replaceChildren(LoadingBlock({ count: 5, label: 'Loading users' }));
    }

    try {
      const page = await api.listAdminUsers({ limit, offset: 0, signal: ownController.signal });
      if (disposed || own !== generation) return;
      items = page.items;
      total = page.total;
      paint();
    } catch {
      if (disposed || own !== generation) return;
      if (quiet) return; // optimistic rows already reflect the applied change
      body.replaceChildren(
        ErrorBlock({
          onRetry: () => {
            void load();
          },
          ...(announce === undefined ? {} : { announce }),
        }),
      );
    }
  }

  /* ── optimistic mutations (AC 2.12; mock `_optim` L1133) ──────────────── */

  /**
   * Applies a row change immediately, calls the API, and on failure ROLLS
   * BACK to the pre-mutation snapshot with an error toast. On success the
   * list quiet-refreshes so server truth (real ids, server timestamps)
   * replaces the optimistic guess.
   *
   * @param {{
   *   apply: () => void,
   *   call: () => Promise<unknown>,
   *   okMessage: string,
   *   failMessage: string,
   *   onFailure?: (error: unknown) => void,
   * }} mutation
   * @returns {Promise<void>}
   */
  async function runMutation({ apply, call, okMessage, failMessage, onFailure }) {
    if (mutating || disposed) return;
    mutating = true;
    // Enter: lock the invite form (not rebuilt by paint) and repaint the rows
    // so their action buttons render disabled for the in-flight window.
    setFormDisabled(true);
    const snapshotItems = items; // `apply` replaces, never mutates in place
    const snapshotTotal = total;
    apply();
    paint();
    try {
      await call();
      if (disposed) return;
      toast(okMessage, 'ok');
      void load({ quiet: true });
    } catch (error) {
      if (disposed) return;
      items = snapshotItems;
      total = snapshotTotal;
      toast(failMessage, 'err');
      if (onFailure !== undefined) onFailure(error);
    } finally {
      mutating = false;
      // Exit: re-enable the form and repaint so the rows' action buttons come
      // back (paint reads the now-cleared `mutating`). A successful mutation
      // also quiet-refreshes, which paints again with server truth.
      if (!disposed) {
        setFormDisabled(false);
        paint();
      }
    }
  }

  /** @param {AdminUserRow} user */
  function roleMutation(user) {
    const nextRole = user.role === 'admin' ? 'member' : 'admin';
    void runMutation({
      apply: () => {
        items = items.map((u) => (u.id === user.id ? { ...u, role: nextRole } : u));
      },
      call: () => api.setAdminUserRole(user.id, nextRole),
      okMessage: ROLE_UPDATED_MESSAGE,
      failMessage: ROLE_FAILED_MESSAGE,
    });
  }

  /** @param {AdminUserRow} user */
  function removeMutation(user) {
    const reactivating = user.status === 'removed';
    void runMutation({
      apply: () => {
        items = items.map((u) =>
          u.id === user.id ? { ...u, status: reactivating ? 'active' : 'removed' } : u,
        );
      },
      call: () =>
        reactivating ? api.reactivateAdminUser(user.id) : api.removeAdminUser(user.id),
      okMessage: reactivating ? USER_REACTIVATED_MESSAGE : USER_REMOVED_MESSAGE,
      failMessage: UPDATE_FAILED_MESSAGE,
    });
  }

  /* ── invite (mock `_addUser` L1126-1128; AC 2.3) ──────────────────────── */

  function submitInvite() {
    // A mutation is in flight: the form is disabled, but a programmatic submit
    // could still reach here. Bail BEFORE touching the field so the typed value
    // is never cleared and lost while runMutation would only early-return.
    if (mutating) return;
    const email = /** @type {HTMLInputElement} */ (input).value.trim();
    if (email === '') {
      showFieldError(EMAIL_REQUIRED_ERROR);
      return;
    }
    if (!EMAIL_PATTERN.test(email)) {
      showFieldError(EMAIL_INVALID_ERROR);
      return;
    }
    const existing = items.find((u) => u.email.toLowerCase() === email.toLowerCase());
    if (existing !== undefined && existing.status !== 'removed') {
      showFieldError(alreadyInvitedError(email));
      return;
    }
    clearFieldError();
    /** @type {HTMLInputElement} */ (input).value = '';

    if (existing !== undefined) {
      // A removed entry: `POST /api/admin/users` reactivates it (mock parity).
      void runMutation({
        apply: () => {
          items = items.map((u) => (u.id === existing.id ? { ...u, status: 'active' } : u));
        },
        call: () => api.addAdminUser(email),
        okMessage: `Reactivated ${email}`,
        failMessage: REACTIVATE_FAILED_MESSAGE,
        onFailure: surfaceInviteFailure,
      });
      return;
    }

    /** @type {AdminUserRow} */
    const optimistic = {
      // Local placeholder — the quiet refresh replaces it with server truth.
      id: `pending-${Date.now()}-${items.length}`,
      email,
      role: 'member',
      status: 'invited',
      invitedAt: new Date().toISOString(),
      lastActiveAt: null,
      linked: false,
      userId: null,
    };
    void runMutation({
      apply: () => {
        items = [...items, optimistic];
        total += 1;
      },
      call: () => api.addAdminUser(email),
      okMessage: `Invited ${email}`,
      failMessage: INVITE_FAILED_MESSAGE,
      onFailure: surfaceInviteFailure,
    });
  }

  /**
   * A server-side 422 carries the actionable text in `fieldErrors.email` —
   * surface it inline next to the field it belongs to (the rollback toast
   * already announced the failure itself).
   *
   * @param {unknown} error
   */
  function surfaceInviteFailure(error) {
    const text = fieldErrorText(error);
    if (text !== null) showFieldError(text);
  }

  /* ── delete-data (AC 2.9) ─────────────────────────────────────────────── */

  /** @param {AdminUserRow} user */
  function openDeleteData(user) {
    openDeleteDataModal({
      overlays,
      email: user.email,
      onConfirm: (confirm) => {
        void (async () => {
          try {
            await api.deleteAdminUserData(user.id, confirm);
            if (disposed) return;
            // mock `_ddm` submit (L1149) — copy verbatim.
            toast(`Personal data purged for ${user.email}`, 'ok');
          } catch (error) {
            if (disposed) return;
            toast(fieldErrorText(error) ?? PURGE_FAILED_MESSAGE, 'err');
          }
        })();
      },
    });
  }

  void load();

  return {
    element: section,
    dispose() {
      disposed = true;
      controller?.abort();
    },
  };
}
